import { ReactNode, useMemo, useState } from "react";
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  Textarea,
  useDisclosure,
} from "@heroui/react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Star, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useCreateReview } from "@/hooks";
import { saveReviewToOfflineQueue } from "@/utils/offlineReviewQueue";

type RatingModalProps = {
  renderTrigger: (open: () => void) => ReactNode;
};

type RatingCategory = "staff" | "service" | "cleanliness";

type FormState = {
  name: string;
  staff: number;
  service: number;
  cleanliness: number;
  overall: number;
  phone: string;
  table: string;
  comment: string;
};

const initialState: FormState = {
  name: "",
  staff: 0,
  service: 0,
  cleanliness: 0,
  overall: 0,
  phone: "",
  table: "",
  comment: "",
};

export const RatingModal = ({ renderTrigger }: RatingModalProps) => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const isAr = locale === "ar" || locale === "ku";
  const createReviewMutation = useCreateReview();

  const [form, setForm] = useState<FormState>(initialState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const labels = useMemo(
    () => ({
      title:
        locale === "ar"
          ? "قيّم تجربتك"
          : locale === "ku"
            ? "ئەزموونەکەت هەڵسەنگێنە"
            : "Rate Your Experience",
      subtitle:
        locale === "ar"
          ? "رأيك يهمّنا"
          : locale === "ku"
            ? "ڕای تۆ گرنگە"
            : "Your feedback matters",
      staff:
        locale === "ar" ? "الطاقم" : locale === "ku" ? "کارمەندان" : "Staff",
      service:
        locale === "ar"
          ? "الخدمة"
          : locale === "ku"
            ? "خزمەتگوزاری"
            : "Service",
      cleanliness:
        locale === "ar" ? "النظافة" : locale === "ku" ? "پاکی" : "Cleanliness",
      overall:
        locale === "ar"
          ? "التقييم العام"
          : locale === "ku"
            ? "هەڵسەنگاندنی گشتی"
            : "Overall Experience",
      name: locale === "ar" ? "الاسم" : locale === "ku" ? "ناو" : "Name",
      phone: locale === "ar" ? "الهاتف" : locale === "ku" ? "مۆبایل" : "Phone",
      table: locale === "ar" ? "الطاولة" : locale === "ku" ? "مێز" : "Table",
      comment:
        locale === "ar"
          ? "تعليق (اختياري)"
          : locale === "ku"
            ? "سەرنج (ئارەزوومەندانە)"
            : "Comment (optional)",
      phonePlaceholder: "05xxxxxxxx",
      namePlaceholder:
        locale === "ar"
          ? "الاسم الكامل"
          : locale === "ku"
            ? "ناوی تەواو"
            : "Full Name",
      tablePlaceholder:
        locale === "ar"
          ? "رقم الطاولة"
          : locale === "ku"
            ? "ژمارەی مێز"
            : "Table #",
      commentPlaceholder:
        locale === "ar"
          ? "شاركنا رأيك..."
          : locale === "ku"
            ? "ڕایەکەت لەگەڵماندا بەشدار بکە..."
            : "Share your thoughts...",
      submit: locale === "ar" ? "إرسال" : locale === "ku" ? "ناردن" : "Submit",
      cancel:
        locale === "ar"
          ? "إلغاء"
          : locale === "ku"
            ? "هەڵوەشاندنەوە"
            : "Cancel",
      thanksTitle:
        locale === "ar"
          ? "شكراً لك! \ud83c\udf89"
          : locale === "ku"
            ? "سوپاس! \ud83c\udf89"
            : "Thank You! \ud83c\udf89",
      thanksBody:
        locale === "ar"
          ? "نقدّر لك وقتك في مشاركة رأيك معنا"
          : locale === "ku"
            ? "سوپاس بۆ کاتی خۆت لە بەشدارکردنی ڕاکەت لەگەڵماندا"
            : "We appreciate you taking the time to share your feedback",
      done: locale === "ar" ? "إغلاق" : locale === "ku" ? "داخستن" : "Close",
    }),
    [locale],
  );

  const emojiOptions = useMemo(
    () => [
      {
        emoji: "\ud83d\ude22",
        label: locale === "ar" ? "سيء" : locale === "ku" ? "خراپ" : "Poor",
      },
      {
        emoji: "\ud83d\ude15",
        label: locale === "ar" ? "مقبول" : locale === "ku" ? "قبووڵە" : "Fair",
      },
      {
        emoji: "\ud83d\ude42",
        label: locale === "ar" ? "جيد" : locale === "ku" ? "باش" : "Good",
      },
      {
        emoji: "\ud83d\ude0a",
        label: locale === "ar" ? "رائع" : locale === "ku" ? "زۆر باش" : "Great",
      },
      {
        emoji: "\ud83e\udd29",
        label:
          locale === "ar" ? "ممتاز" : locale === "ku" ? "باشترین" : "Excellent",
      },
    ],
    [locale],
  );

  const setCategoryRating = (category: RatingCategory, value: number) => {
    setForm((prev) => ({ ...prev, [category]: value }));
  };

  const canSubmit =
    form.name.trim() !== "" &&
    form.staff > 0 &&
    form.service > 0 &&
    form.cleanliness > 0 &&
    form.overall > 0;

  const resetAll = () => {
    setForm(initialState);
    setIsSubmitting(false);
    setIsSubmitted(false);
  };

  const handleSubmit = async () => {
    if (!canSubmit || isSubmitting) return;

    // 1. If offline, save immediately to localStorage queue in 0ms without waiting or spinning
    if (typeof navigator !== "undefined" && !navigator.onLine) {
      saveReviewToOfflineQueue({
        customer_name: form.name,
        rating: form.overall,
        review_text:
          form.comment ||
          `Staff: ${form.staff}/5, Service: ${form.service}/5, Cleanliness: ${form.cleanliness}/5`,
        type: "branch",
        lat: null,
        lan: null,
      });
      setIsSubmitted(true);
      return;
    }

    setIsSubmitting(true);

    let lat: number | null = null;
    let lan: number | null = null;

    // Only attempt geolocation if online and with a tight timeout to avoid hanging
    if (typeof navigator !== "undefined" && navigator.onLine && navigator.geolocation) {
      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, {
            maximumAge: 60000,
            timeout: 500,
          });
        });
        lat = position.coords.latitude;
        lan = position.coords.longitude;
      } catch {
        // Ignore location errors
      }
    }

    try {
      await createReviewMutation.mutateAsync({
        customer_name: form.name,
        rating: form.overall,
        review_text:
          form.comment ||
          `Staff: ${form.staff}/5, Service: ${form.service}/5, Cleanliness: ${form.cleanliness}/5`,
        type: "branch",
        lat,
        lan,
      });
      setIsSubmitted(true);
    } catch {
      // Still show submitted screen if saved offline
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const Stars = ({
    value,
    onChange,
    size = "md",
  }: {
    value: number;
    onChange: (val: number) => void;
    size?: "sm" | "md";
  }) => (
    <div
      className="flex items-center justify-center gap-1 sm:gap-1.5 shrink-0"
      dir="ltr"
    >
      {Array.from({ length: 5 }).map((_, i) => {
        const starValue = i + 1;
        const active = value >= starValue;
        return (
          <motion.button
            key={starValue}
            type="button"
            onClick={() => onChange(starValue)}
            whileTap={{ scale: 0.85 }}
            className="focus:outline-none p-1"
            aria-label={`rate-${starValue}`}
          >
            <Star
              className={`${size === "sm" ? "size-7 sm:size-8" : "size-8 sm:size-9"} transition-all duration-200 ${
                active
                  ? "text-amber-400 fill-amber-400 drop-shadow-sm"
                  : "text-gray-300"
              }`}
            />
          </motion.button>
        );
      })}
    </div>
  );

  const RatingRow = ({
    label,
    value,
    onChange,
  }: {
    label: string;
    value: number;
    onChange: (v: number) => void;
  }) => (
    <div className="flex items-center justify-between py-3.5 gap-3 min-w-0">
      <span className="text-base sm:text-lg font-semibold text-primary/90 truncate">
        {label}
      </span>
      <Stars value={value} onChange={onChange} size="sm" />
    </div>
  );

  return (
    <>
      {renderTrigger(onOpen)}

      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open) resetAll();
          onOpenChange();
        }}
        placement="center"
        backdrop="blur"
        size="lg"
        hideCloseButton
        scrollBehavior="inside"
        classNames={{
          base: "mx-3 sm:mx-4 my-auto max-h-[85vh] sm:max-h-[80vh] overflow-y-auto overflow-x-hidden rounded-2xl bg-mainBg shadow-xl border-0 w-full max-w-[420px]",
          body: "p-0 overflow-x-hidden",
          wrapper: "items-center",
        }}
      >
        <ModalContent>
          {(onClose) => (
            <AnimatePresence mode="wait">
              {!isSubmitted ? (
                <motion.div
                  key="form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {/* Header */}
                  <div className="relative bg-linear-to-br from-primary to-primary/80 px-4 py-4 text-center shrink-0">
                    <button
                      onClick={onClose}
                      className="absolute top-3 end-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                    >
                      <X className="size-4 text-[#EAE4DB]" />
                    </button>
                    <h2 className="text-xl sm:text-2xl font-bold text-[#EAE4DB]">
                      {labels.title}
                    </h2>
                    <p className="text-sm text-[#EAE4DB]/80 mt-1">
                      {labels.subtitle}
                    </p>
                  </div>

                  <ModalBody className="px-5 sm:px-6 py-5 sm:py-6 space-y-5 overflow-x-hidden">
                    {/* Ratings Grid */}
                    <div className="divide-y divide-primary/10 overflow-x-hidden">
                      <RatingRow
                        label={labels.staff}
                        value={form.staff}
                        onChange={(v) => setCategoryRating("staff", v)}
                      />
                      <RatingRow
                        label={labels.service}
                        value={form.service}
                        onChange={(v) => setCategoryRating("service", v)}
                      />
                      <RatingRow
                        label={labels.cleanliness}
                        value={form.cleanliness}
                        onChange={(v) => setCategoryRating("cleanliness", v)}
                      />
                    </div>

                    {/* Overall Experience */}
                    <div className="bg-primary/5 rounded-2xl p-4 text-center overflow-x-hidden">
                      <p className="text-base font-semibold text-primary/80 mb-4">
                        {labels.overall}
                      </p>
                      <div
                        className="flex items-center justify-center gap-3 sm:gap-4"
                        dir="ltr"
                      >
                        {emojiOptions.map((item, i) => {
                          const selected = form.overall === i + 1;
                          return (
                            <motion.button
                              key={item.emoji}
                              type="button"
                              onClick={() =>
                                setForm((prev) => ({ ...prev, overall: i + 1 }))
                              }
                              whileTap={{ scale: 0.9 }}
                              className={`text-3xl sm:text-4xl transition-all duration-200 ${
                                selected
                                  ? "scale-125 drop-shadow-lg"
                                  : form.overall > 0
                                    ? "opacity-40 grayscale"
                                    : "opacity-70 hover:opacity-100"
                              }`}
                            >
                              {item.emoji}
                            </motion.button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Contact Info */}
                    <div className="space-y-3">
                      <Input
                        size="lg"
                        placeholder={labels.namePlaceholder}
                        value={form.name}
                        onValueChange={(v) =>
                          setForm((p) => ({ ...p, name: v }))
                        }
                        variant="flat"
                        classNames={{
                          inputWrapper: "bg-primary/5 border-0 shadow-none h-14",
                          input:
                            "text-base text-primary placeholder:text-primary/40",
                        }}
                        startContent={
                          <span className="text-sm text-primary font-medium whitespace-nowrap">
                            {locale === "ar"
                              ? "👤 اسم"
                              : locale === "ku"
                                ? "👤 ناو"
                                : "👤 Name"}
                          </span>
                        }
                      />
                      <div className="grid grid-cols-1 gap-3">
                        <Input
                          size="lg"
                          placeholder={labels.phonePlaceholder}
                          value={form.phone}
                          onValueChange={(v) =>
                            setForm((p) => ({ ...p, phone: v }))
                          }
                          variant="flat"
                          classNames={{
                            inputWrapper: "bg-primary/5 border-0 shadow-none h-14",
                            input:
                              "text-base text-primary placeholder:text-primary/40",
                          }}
                          dir="ltr"
                          startContent={
                            <span className="text-sm text-primary font-medium whitespace-nowrap">
                              {locale === "ar"
                                ? "📱 هاتف"
                                : locale === "ku"
                                  ? "📱 تەلەفۆن"
                                  : "📱 Phone"}
                            </span>
                          }
                        />
                        <Input
                          size="lg"
                          placeholder={labels.tablePlaceholder}
                          value={form.table}
                          onValueChange={(v) =>
                            setForm((p) => ({ ...p, table: v }))
                          }
                          variant="flat"
                          classNames={{
                            inputWrapper: "bg-primary/5 border-0 shadow-none h-14",
                            input:
                              "text-base text-primary placeholder:text-primary/40",
                          }}
                          startContent={
                            <span className="text-sm text-primary font-medium whitespace-nowrap">
                              {locale === "ar"
                                ? "🍽️ طاولة"
                                : locale === "ku"
                                  ? "🍽️ مێز"
                                  : "🍽️ Table"}
                            </span>
                          }
                        />
                      </div>
                    </div>

                    {/* Comment */}
                    <Textarea
                      size="lg"
                      placeholder={labels.commentPlaceholder}
                      value={form.comment}
                      onValueChange={(v) =>
                        setForm((p) => ({ ...p, comment: v }))
                      }
                      variant="flat"
                      minRows={3}
                      maxRows={4}
                      classNames={{
                        inputWrapper: "bg-primary/5 border-0 shadow-none",
                        input:
                          "text-base text-primary placeholder:text-primary/40",
                      }}
                    />

                    {/* Actions */}
                    <div className="flex flex-col gap-3 pt-1">
                      <Button
                        color="primary"
                        className="w-full font-bold text-base"
                        size="lg"
                        isDisabled={!canSubmit}
                        isLoading={
                          isSubmitting || createReviewMutation.isPending
                        }
                        onPress={handleSubmit}
                      >
                        {labels.submit}
                      </Button>
                      <Button
                        variant="flat"
                        className="w-full bg-primary/5 text-primary/70 font-medium text-base"
                        size="lg"
                        onPress={onClose}
                      >
                        {labels.cancel}
                      </Button>
                    </div>
                  </ModalBody>
                </motion.div>
              ) : (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-10 px-6 text-center"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: "spring", delay: 0.1 }}
                  >
                    <CheckCircle2 className="size-16 text-green-500 mx-auto mb-4" />
                  </motion.div>
                  <h3 className="text-2xl font-bold text-primary mb-3">
                    {labels.thanksTitle}
                  </h3>
                  <p className="text-base text-primary/70 mb-6">
                    {labels.thanksBody}
                  </p>
                  <Button
                    color="primary"
                    className="font-semibold px-8"
                    onPress={() => {
                      onClose();
                      resetAll();
                    }}
                  >
                    {labels.done}
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
